import type { ClientMessage, ErrorCode, Role, RoomView } from '@chipless/shared';
import { create } from 'zustand';
import type { ConnectionStatus } from './connection.js';
import { Connection } from './connection.js';

/**
 * クライアントが保持するのは `RoomView` 1つだけ（design 9.3）。
 *
 * 派生表示はレンダリング時に計算し、ローカルな複製状態を作らない。楽観的更新は
 * 行わない。サーバ往復は通常1秒以内に収まり、楽観的更新が生む「一瞬だけ間違った金額が
 * 見える」事故のほうが実害が大きい。
 */

export type ClientError = { code: ErrorCode; message: string };

type Store = {
  view: RoomView | null;
  status: ConnectionStatus;
  error: ClientError | null;
  /** 送信中はアクションボタンを無効化し、二重送信を UI 層でも抑止する。 */
  pending: boolean;

  connect: (params: { roomCode: string; token: string; role: Role }) => void;
  disconnect: () => void;
  send: (message: ClientMessage) => void;
  clearError: () => void;
};

/** 接続はモジュール内に1つだけ持つ。状態ストアには入れない。 */
let connection: Connection | null = null;
let currentKey: string | null = null;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

const PENDING_TIMEOUT_MS = 5000;

export const useStore = create<Store>((set, get) => ({
  view: null,
  status: 'closed',
  error: null,
  pending: false,

  connect: ({ roomCode, token, role }) => {
    const key = `${roomCode}|${token}|${role}`;
    if (connection !== null && currentKey === key) return;

    connection?.stop();
    currentKey = key;
    set({ view: null, error: null, pending: false });

    connection = new Connection(
      { roomCode, token, role },
      {
        onState: (view) => set({ view, pending: false }),
        onError: (error) => set({ error, pending: false }),
        onStatus: (status) => {
          // 切断中は送信できないため、保留状態を解除しておく。
          set(status === 'open' ? { status } : { status, pending: false });
        },
      },
    );
    connection.start();
  },

  disconnect: () => {
    connection?.stop();
    connection = null;
    currentKey = null;
    set({ view: null, status: 'closed', pending: false });
  },

  send: (message) => {
    if (get().pending) return;
    const sent = connection?.send(message) ?? false;
    if (!sent) {
      set({
        error: { code: 'INTERNAL', message: '接続が切れています。再接続をお待ちください' },
      });
      return;
    }
    // 状態が返るかエラーが返るまで操作を止める。
    set({ pending: true, error: null });

    // 応答が届かないまま操作不能になるのを防ぐ。サーバは成功でも失敗でも必ず返すので、
    // これは経路が落ちた場合の保険。
    if (pendingTimer !== null) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      if (get().pending) set({ pending: false });
    }, PENDING_TIMEOUT_MS);
  },

  clearError: () => set({ error: null }),
}));
